import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { filterSearchPages } from "@/lib/navigationSearch";
import { cn } from "@/lib/utils";

function isMacPlatform() {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);
}

function ShortcutKeys() {
  const mac = isMacPlatform();

  return (
    <span className="inline-flex items-center gap-0.5 shrink-0 select-none" aria-hidden="true">
      <kbd className="kbd">{mac ? "⌘" : "Ctrl"}</kbd>
      <span className="text-[10px] text-slate-400 font-medium leading-none px-0.5">+</span>
      <kbd className="kbd">K</kbd>
    </span>
  );
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const results = useMemo(
    () => filterSearchPages(query, 10, permissions),
    [query, permissions],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    function onKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        });
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    function onPointerDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  function go(to) {
    navigate(to);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  }

  function handleInputKeyDown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (!open || results.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = results[activeIndex];
      if (target) go(target.to);
    }
  }

  return (
    <div ref={containerRef} className="hidden xl:block relative w-[300px] shrink-0">
      <div
        className={cn(
          "flex items-center gap-2 bg-slate-50 border border-[#E5E7EB] rounded-md h-9 pl-2.5 pr-2",
          "focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-300",
        )}
      >
        <Search size={14} className="text-slate-400 shrink-0" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleInputKeyDown}
          placeholder="Search pages…"
          className="flex-1 min-w-0 h-7 bg-transparent text-[13px] outline-none placeholder:text-slate-400"
          data-testid="global-search"
          aria-label="Search pages"
          aria-expanded={open}
          aria-controls="global-search-results"
          autoComplete="off"
        />
        <ShortcutKeys />
      </div>

      {open && (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50 py-1 max-h-[320px] overflow-y-auto"
        >
          {results.length === 0 ? (
            <div className="px-3 py-4 text-[13px] text-slate-400 text-center">No matching pages</div>
          ) : (
            results.map((item, index) => (
              <button
                key={item.to}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                className={cn(
                  "w-full text-left px-3 py-2 transition-colors",
                  index === activeIndex ? "bg-blue-50" : "hover:bg-slate-50",
                )}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => go(item.to)}
              >
                <div className="text-[13px] font-medium text-slate-800">{item.label}</div>
                <div className="text-[11px] text-slate-400">{item.group}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
